import {DefaultPageProps} from "@models/DefaultProps.ts";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import {useContrato} from "@components/contratos/hooks/useContrato.tsx";
import {IContrato} from "@models/contratos/contrato.ts";
import {useEffect, useState} from "react";
import {Stack, Button, ButtonGroup, Grid, IconButton, Paper, TextField, Typography} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import dayjs, {Dayjs} from "dayjs";
import {MoneyInputEntry} from "@shared/specialComponents/InputEntry.tsx";
import {ICreateContratoParcelaDto} from "@components/contratos/models/ICreateContratoParcelaDto.ts";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {
	Delete, Add, Save
} from "@mui/icons-material";

interface CreateParcelasProps extends DefaultPageProps {
	preset: {
		contrato: IContrato | number,
		proximaData?: Dayjs
	},
	onCreate?: (parcela: IContrato) => void;
}

export function CreateParcelas(props: CreateParcelasProps) {
	const {useContratoValue, createParcela} = useContrato()
	const {createAlertFromResponse, addPopUp, closePopUp} = useAlert()
	const [contrato] = useContratoValue(props.preset.contrato)

	const [parcelas, setParcelas] = useState<ICreateContratoParcelaDto[]>([]);

	useEffect(() => {
		if (contrato != null) {
			setParcelas([{
				idContrato: contrato.id,
				valor: 0,
				dataVencimento: props.preset.proximaData?.isAfter('1900-01-01', 'date') && props.preset.proximaData || dayjs()
			}])
		}
	}, [contrato]);

	const addParcela = () => {
		setParcelas(prev => {
			if (prev.length === 0) {
				return [{
					idContrato: contrato?.id ?? 0,
					valor: 0,
					dataVencimento: props.preset.proximaData?.isAfter('1900-01-01', 'date') && props.preset.proximaData || dayjs()
				}];
			}
			const lastParcela = prev[prev.length - 1];
			const newParcela: ICreateContratoParcelaDto = {
				idContrato: lastParcela.idContrato,
				valor: lastParcela.valor,
				dataVencimento: dayjs(lastParcela.dataVencimento).add(1, 'month'),
				observacao: lastParcela.observacao
			};
			return [...prev, newParcela];
		});
	};

	const handleParcelaChange = (index: number, field: keyof ICreateContratoParcelaDto, value: any) => {
		setParcelas(prev => {
			const newParcelas = [...prev];
			newParcelas[index] = {...newParcelas[index], [field]: value};
			return newParcelas;
		});
	};

	const handleRemoveParcela = (index: number) => {
		const parcela = parcelas[index];
		const hasValor = (parcela?.valor ?? 0) > 0;
		const hasObs = (parcela?.observacao ?? '').toString().trim().length > 0;
		if (hasValor || hasObs) {
			addPopUp({
				title: 'Confirmar exclusão de parcela',
				content: <Grid container spacing={2}>
					<Grid size={12}>
						<Typography>Esta parcela possui valor ou observação preenchidos. Tem certeza que deseja excluí-la?</Typography>
					</Grid>
					<Grid size={12} container justifyContent={'flex-end'}>
						<ButtonGroup>
							<Button color={'warning'} onClick={() => {
								setParcelas(prev => prev.filter((_, i) => i !== index));
								closePopUp();
							}}>
								Sim, excluir
							</Button>
							<Button color={'info'} onClick={() => closePopUp()}>Não, voltar</Button>
						</ButtonGroup>
					</Grid>
				</Grid>
			})
			return;
		}
		setParcelas(prev => prev.filter((_, i) => i !== index));
	};

	const handleSubmit = () => {
		parcelas.forEach(parcela => {
			createParcela(parcela).then(x => {
				createAlertFromResponse(x);
			});
		});
	};

	const isValorOk = (valor: number) => valor > 0;
	const isDataOk = (data?: Dayjs) => dayjs(data).isAfter(dayjs().add(-1, "day"), 'date');


	return (<DefaultContainer titulo={"Inclusao de parcela"}
	                          hideHeader={props.hideHeader}>
		<Grid container spacing={2}>
			{parcelas.map((parcela, index) => (
				<Grid size={{xs: 12}} key={index}>
					<Paper elevation={2} sx={{p: 2}}>
						<Grid container spacing={2} alignItems="center">
							<Grid size={{xs: 12, sm: 3}}>
								<MoneyInputEntry label={'Valor'}
								                 value={parcela.valor}
								                 textFieldProps={{
									                 error: !isValorOk(parcela.valor),
									                 fullWidth: true
								                 }}
								                 onChange={(v) => handleParcelaChange(index, 'valor', v ?? 0)}/>
							</Grid>
							<Grid size={{xs: 12, sm: 3}}>
								<DatePicker value={dayjs(parcela.dataVencimento)}
								            label={'Vencimento'}
								            slotProps={{
									            textField: {
										            error: !isDataOk(parcela.dataVencimento),
										            fullWidth: true
									            }
								            }}
								            onChange={(v) => handleParcelaChange(index, 'dataVencimento', v ?? dayjs())}/>
							</Grid>
							<Grid size={{xs: 12, sm: 5}}>
								<TextField label={'Observações'}
								           value={parcela.observacao ?? ''}
								           fullWidth
								           onChange={(e) => handleParcelaChange(index, 'observacao', e.target.value)}/>
							</Grid>
							<Grid size={{xs: 12, sm: 1}}>
								<IconButton onClick={() => handleRemoveParcela(index)}>
									<Delete/>
								</IconButton>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
			))}
			<Grid size={{xs: 12}}>
				<Stack direction="row" spacing={1} justifyContent="flex-end">
					<ButtonGroup>
						<Button onClick={addParcela} color={'secondary'} variant="outlined" startIcon={<Add/>}>
							Adicionar Parcela
						</Button>
						<Button onClick={handleSubmit} color={'success'} variant="contained" startIcon={<Save/>}>
							Salvar novas parcelas
						</Button>
					</ButtonGroup>
				</Stack>
			</Grid>
		</Grid>

	</DefaultContainer>)
}